import { useEffect, useMemo, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Box, Checkbox, ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper, Radio, TextField } from '@mui/material';
import _ from 'lodash';
import { styles, SelectButtonContainer, SelectButton, ClearButton } from './styles';

export default function AxlMUISelect({
  single = false,
  showAll = false,
  onChange = () => {/* empty */},
  placeholder = 'Select value',
  searchPlaceholder = 'Search here...',
  placeholderSize = 14,
  ...props
}) {
  // global
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [checkedValues, setCheckedValues] = useState([]);
  const [options, setOptions] = useState(props.options || []);
  // Defined - use props.options (full unfiltered list) for label lookup so searching doesn't hide selected labels
  const fullOptions = props.options || [];
  const _isValues = checkedValues
    .map((value) => fullOptions.find((option) => option.value === value))
    .filter(Boolean)
    .map((option) => option.label);
  const _isShow = showAll ? _isValues.join(', ') : `${_isValues.length} selected`;

  function handleCheck(checkedName) {
    let newNames = [];
    if (single) {
      newNames = [checkedName];
    } else {
      newNames = checkedValues?.includes(checkedName)
        ? checkedValues?.filter((name) => name !== checkedName)
        : [...(checkedValues ?? []), checkedName];
    }
    setCheckedValues(newNames);
    if (onChange) onChange(newNames);
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  // Memoize the debounced search function to avoid recreating on every keystroke
  const debouncedSearch = useMemo(
    () =>
      _.debounce((searchValue) => {
        const fullList = props.options || [];
        if (searchValue && searchValue.trim()) {
          const filtered = _.filter(fullList, (option) =>
            _.includes(option.label.toLowerCase(), searchValue.trim().toLowerCase())
          );
          setOptions(filtered);
        } else {
          // Restore full list when search is empty
          setOptions(fullList);
        }
      }, 300),
    [props.options]
  );

  // Cleanup debounced function on unmount to avoid setState after unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSearch = ({ target: { value } }) => {
    debouncedSearch(value);
  };

  const clearValues = () => {
    setCheckedValues([]);
    setOptions(props.options || []);
    onChange([]);
  };

  // Sync local options state with props.options when it changes
  useEffect(() => {
    setOptions(props.options || []);
  }, [props.options]);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Box sx={styles.root}>
      <SelectButtonContainer
        ref={anchorRef}
        tabIndex={0}
        role="button"
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        open={open}
      >
        <SelectButton fontSize={placeholderSize}>{checkedValues.length ? _isShow : placeholder}</SelectButton>
        <ArrowDropUpIcon style={{ display: !open ? 'none' : 'block' }} color="primary" />
        <ArrowDropDownIcon style={{ display: open ? 'none' : 'block' }} color="primary" />
      </SelectButtonContainer>
      <IconButton sx={styles.clearButton} onClick={clearValues} size="small">
        <ClearButton fontSize={'small'} />
      </IconButton>
      <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} sx={styles.menuList}>
                  <Box>
                    <TextField onChange={onSearch} placeholder={searchPlaceholder} size="small" fullWidth />
                  </Box>
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {options.map((option, idx) => (
                      <MenuItem key={`option-${idx}`} sx={styles.listItem} onClick={() => handleCheck(option.value)}>
                        {!single ? (
                          <Checkbox
                            key={`checkbox-${idx}`}
                            value={option.value}
                            checked={_.includes(checkedValues, option.value)}
                          />
                        ) : (
                          <Radio checked={_.includes(checkedValues, option.value)} value={option.value} />
                        )}
                        {option.label}
                      </MenuItem>
                    ))}
                  </div>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}
